import React, { useEffect, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import AccordionTitleIcon from "../common/Icons/AccordionTitleIcon";
import AccordionClickIcon from "../common/Icons/AccordionClickIcon";
import { useTrackContext } from "../../hooks/useTrackContext";
import { useLocation } from "react-router-dom";

interface Section {
  title: string;
  content: React.ReactNode;
}

interface ModalWithAccordionProps {
  trackTitle: "MainTrack" | "MiniTrack" | "VirtualReality" | "JuniorTrack" | "BattleKart";
  title: string;
  sections: Section[];
}

const ModalWithAccordion: React.FC<ModalWithAccordionProps> = ({ trackTitle, title, sections }) => {
  const location = useLocation();

  // Check if battle kart
  const isBattleKart = location.pathname.includes("battle-kart");

  const { mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, battleKartSelection, dispatch, appHostedAt } = useTrackContext();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(() => {
    switch (location.pathname) {
      case "/main-track/booking-summary":
        return mainTrackSelection.isTermsAndConditionExcepted ? false : true;
      case "/mini-track/booking-summary":
        return miniTrackSection.isTermsAndConditionExcepted ? false : true;
      case "/virtual-reality/booking-summary":
        return vrSelection.isTermsAndConditionExcepted ? false : true;
      case "/junior-track/booking-summary":
        return juniorTrackSelection.isTermsAndConditionExcepted ? false : true;
      case "/battle-kart/booking-summary":
        return battleKartSelection.isTermsAndConditionExcepted ? false : true;
      default:
        return false;
    }
  });

  const [accordionStates, setAccordionStates] = useState<boolean[]>(Array.isArray(sections) ? sections.map(() => false) : []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isModalOpen]);

  const [isChecked, setIsChecked] = useState<boolean>(() => {
    switch (trackTitle) {
      case "MainTrack":
        return mainTrackSelection.isTermsAndConditionExcepted;
      case "MiniTrack":
        return miniTrackSection.isTermsAndConditionExcepted;
      case "VirtualReality":
        return vrSelection.isTermsAndConditionExcepted;
      case "JuniorTrack":
        return juniorTrackSelection.isTermsAndConditionExcepted;
      case "BattleKart":
        return battleKartSelection.isTermsAndConditionExcepted;
      default:
        return false;
    }
  });
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const toggleAccordion = (index: number) => {
    setAccordionStates((prevStates) => {
      const newState = prevStates.map((state, i) => (i === index ? !state : false));
      return newState;
    });
  };

  const handleTermsAndCondition = () => {
    if (!isChecked) {
      const updatedIsChecked = true;
      setIsChecked(updatedIsChecked);
      switch (trackTitle) {
        case "MainTrack":
          return dispatch({
            type: "SET_TERMS_AND_CONDITION",
            payload: {
              isTermsAndConditionExcepted: updatedIsChecked,
            },
          });
        case "MiniTrack":
          return dispatch({
            type: "SET_TERMS_AND_CONDITION_MINI_TRACK",
            payload: {
              isTermsAndConditionExcepted: updatedIsChecked,
            },
          });
        case "VirtualReality":
          return dispatch({
            type: "SET_TERMS_AND_CONDITION_VIRTUAL_REALITY",
            payload: {
              isTermsAndConditionExcepted: updatedIsChecked,
            },
          });
        case "JuniorTrack":
          return dispatch({
            type: "SET_TERMS_AND_CONDITION_JUNIOR_TRACK",
            payload: {
              isTermsAndConditionExcepted: updatedIsChecked,
            },
          });
        case "BattleKart":
          return dispatch({
            type: "SET_TERMS_AND_CONDITION_BATTLE_KART",
            payload: {
              isTermsAndConditionExcepted: updatedIsChecked,
            },
          });
        default:
          return null;
      }
    }
  };

  return (
    <>
      <div className={`tw-relative lg:tw-absolute tw-right-0 tw-bottom-0`}>
        <button
          id="terms-and-conditions"
          className={`tw-border-[2px] ${isBattleKart ? "tw-border-[#EA581A] tw-bg-[#EA581A]" : "tw-border-[#F609FF] tw-bg-[#C53CFF]"} tw-p-[12px] tw-rounded-full`}
          onClick={() => toggleModal()}
        >
          <AccordionClickIcon clipathId="something_unique_string" />
        </button>
      </div>
      {isModalOpen && (
        <div className="tw-fixed tw-z-[1000] tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-900 tw-bg-opacity-50 tw-p-[10px] lg:tw-p-0">
          <div
            className={`tw-bg-white tw-max-w-[998px] tw-bg-white tw-max-h-[80vh] tw-overflow-y-scroll tw-h-fit tw-w-full tw-mx-auto tw-rounded-[25px] tw-shadow-lg tw-transform tw-transition-all tw-duration-300 tw-scale-100`}
          >
            <div className="tw-px-[10px] tw-pb-[20px] lg:tw-px-[40px]" id="hk-accordion-contents">
              <div className="tw-flex lg:tw-justify-center tw-items-center tw-relative tw-py-[20px]">
                <div className="tw-text-black tw-text-[20px] tw-font-[700]">{title}</div>
                <div className="tw-absolute tw-right-0">
                  <button className="tw-text-gray-600 tw-text-lg tw-hover:text-gray-800 tw-focus:tw-outline-none" onClick={toggleModal}>
                    <AiOutlineClose className="tw-w-[30px] tw-h-[30px] tw-text-[#C2C0C3] tw-font-[400]" />
                  </button>
                </div>
              </div>
              <div className="tw-space-y-4">
                {sections.map((section, index) => (
                  <div key={index} className={`${accordionStates[index] ? "tw-bg-[#d1d5db] tw-rounded-[5px] tw-overflow-hidden" : "tw-bg-white"}`}>
                    <div
                      className="tw-w-full tw-flex tw-justify-between tw-items-center tw-bg-white tw-text-black tw-border-[1px] tw-border-[#C2C0C3] tw-py-[15px] tw-px-[20px] tw-rounded-[5px] focus:tw-outline-none hover:tw-cursor-pointer"
                      onClick={() => toggleAccordion(index)}
                    >
                      <span className="tw-flex tw-items-center tw-gap-[15px]">
                        <div className="tw-hidden lg:tw-block">
                          <AccordionTitleIcon />
                        </div>
                        <div className="tw-block lg:tw-hidden">
                          <AccordionTitleIcon width={section.title.length > 24 ? 22 : 22} height={section.title.length > 24 ? 22 : 25} />
                        </div>
                        <span className="tw-text-[14] tw-font-[600] tw-text-black tw-text-left">{section.title}</span>
                      </span>
                      <div className="tw-p-[2px] tw-rounded-full tw-bg-[#F4F4F4] tw-flex tw-justify-between tw-items-center">
                        <svg
                          className={`tw-w-[18px] tw-h-[18px] tw-transform tw-transition-transform tw-duration-200 ${accordionStates[index] ? "tw-rotate-180" : "tw-rotate-0"}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                    {accordionStates[index] && (
                      <div
                        className="tw-bg-[#e2e8f0] tw-max-h-[300px] tw-h-full tw-overflow-auto tw-border-[1px] tw-border-[#C2C0C3] tw-p-[20px]"
                        dangerouslySetInnerHTML={{
                          __html: section.content as string,
                        }}
                      />
                    )}
                  </div>
                ))}
                <div className="tw-flex tw-items-center tw-gap-[15px] tw-bg-white tw-text-black tw-border-[1px] tw-border-[#C2C0C3] tw-py-[15px] tw-px-[20px] tw-rounded-[5px]">
                  <div className="tw-hidden lg:tw-block">
                    <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="none">
                      <path
                        d="M0.000107847 11.9999C0.00010842 5.45403 5.30532 0.150008 11.85 0.150008C18.3947 0.150009 23.7 5.45403 23.7 11.9999C23.7 18.5435 18.3947 23.8499 11.85 23.8499C5.30532 23.8499 0.000107275 18.5435 0.000107847 11.9999Z"
                        fill="#E31C1C"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.8201 12.7482C10.8201 13.3193 11.2858 13.785 11.8569 13.785C12.4281 13.785 12.8938 13.3193 12.8938 12.7482L12.8938 7.5105C12.8938 6.93933 12.4281 6.47363 11.8569 6.47363C11.2858 6.47363 10.8201 6.93933 10.8201 7.5105L10.8201 12.7482ZM12.8884 16.5077C12.8884 15.9366 12.425 15.4709 11.8574 15.4709C11.2732 15.4709 10.8087 15.9366 10.8087 16.5077C10.8087 17.0789 11.2732 17.5446 11.8456 17.5446C12.4203 17.5446 12.8884 17.0789 12.8884 16.5077Z"
                        fill="white"
                      />
                    </svg>
                  </div>

                  <div className="tw-block lg:tw-hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" width={22} height={22} viewBox="0 0 24 24" fill="none">
                      <path
                        d="M0.000107847 11.9999C0.00010842 5.45403 5.30532 0.150008 11.85 0.150008C18.3947 0.150009 23.7 5.45403 23.7 11.9999C23.7 18.5435 18.3947 23.8499 11.85 23.8499C5.30532 23.8499 0.000107275 18.5435 0.000107847 11.9999Z"
                        fill="#E31C1C"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.8201 12.7482C10.8201 13.3193 11.2858 13.785 11.8569 13.785C12.4281 13.785 12.8938 13.3193 12.8938 12.7482L12.8938 7.5105C12.8938 6.93933 12.4281 6.47363 11.8569 6.47363C11.2858 6.47363 10.8201 6.93933 10.8201 7.5105L10.8201 12.7482ZM12.8884 16.5077C12.8884 15.9366 12.425 15.4709 11.8574 15.4709C11.2732 15.4709 10.8087 15.9366 10.8087 16.5077C10.8087 17.0789 11.2732 17.5446 11.8456 17.5446C12.4203 17.5446 12.8884 17.0789 12.8884 16.5077Z"
                        fill="white"
                      />
                    </svg>
                  </div>

                  <span className="tw-text-[14px] tw-font-[600] tw-text-black tw-text-left">ZERO TOLERANCE TOWARDS DRUGS & ALCOHOL</span>
                </div>
              </div>
              <div
                className="tw-flex tw-items-center tw-gap-[10px] tw-mt-[30px] hover:tw-cursor-pointer"
                onClick={() => handleTermsAndCondition()}
                id="terms-and-condition-wrapper"
              >
                <div className="hover:tw-cursor-pointer">
                  {isChecked ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="tw-w-[23px] tw-h-[23px]" viewBox="0 0 23 23" fill="none">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M16.1677 0.490601H5.84986C2.25425 0.490601 0 3.03645 0 6.6392V16.3608C0 19.9635 2.24354 22.5094 5.84986 22.5094H16.1666C19.7741 22.5094 22.0188 19.9635 22.0188 16.3608V6.6392C22.0188 3.03645 19.7741 0.490601 16.1677 0.490601Z"
                          fill={isBattleKart ? "#EA581A" : "#9D62FE"}
                        />
                        <path d="M6.77173 11.4999L9.59728 14.3243L15.246 8.67554" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <div className={`tw-w-[23px] tw-h-[23px] ${isBattleKart ? "tw-border-[#EA581A]" : "tw-border-[#9D62FE]"} tw-border-[2px] tw-rounded-[6px]`} />
                    </>
                  )}
                </div>
                <p
                  className="tw-text-[14px] tw-text-[#393B41]"
                  style={appHostedAt && appHostedAt === "bosshunting" ? { textDecoration: "none", width: "unset", height: "unset", margin: "unset", fontWeight: 500 } : {}}
                >
                  Accept the Terms and Conditions
                </p>
              </div>

              <button
                onClick={toggleModal}
                className={`tw-mt-[10px] lg:tw-mb-0 tw-mx-auto tw-flex tw-justify-center tw-items-center tw-w-fit tw-h-fit tw-py-[14px] tw-px-[40px] tw-font-bold tw-text-[14px] tw-rounded-[100px] tw-text-white ${
                  isBattleKart ? "add-grobold-font tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]" : "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalWithAccordion;
